import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesAPI, departmentsAPI, clmAPI, assignmentsAPI } from '../services/api';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { RoleBadge } from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import PageHeader from '../components/ui/PageHeader';
import HelpTooltip from '../components/ui/HelpTooltip';
import SearchableSelect from '../components/ui/SearchableSelect';
import Input from '../components/ui/Input';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import StepIndicator from '../components/ui/StepIndicator';
import ConfirmModal from '../components/ui/ConfirmModal';
import Modal from '../components/ui/Modal';
import { getCreatableCourseTypes } from '../utils/coursePermissions';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { usePageTour } from '../components/onboarding/PageTour';

const BookOpenIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const WarningIcon = () => (
  <svg
    className="w-4 h-4 text-warning-600 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const UserEmptyIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const BuildingEmptyIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const ClearFiltersIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const StepNumber = ({ number }) => (
  <span className="inline-flex items-center justify-center w-6 h-6 bg-primary-600 text-white rounded-full text-xs font-bold mr-2 flex-shrink-0">
    {number}
  </span>
);

const AssignCourses = ({ scopedCompanyId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const embedded = Boolean(scopedCompanyId);

  // Preselecție din URL (ex: link „Trimite IP" de pe dashboard):
  // /assign-courses?employeeId=X&companyId=Y
  const preselectEmployeeId = searchParams.get('employeeId');
  const preselectCompanyId = searchParams.get('companyId');
  const [courses, setCourses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);

  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(
    scopedCompanyId || preselectCompanyId || ''
  );
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [dueDate, setDueDate] = useState('');
  // Popup „Trimite și fișa postului" — apare DUPĂ review, doar dacă printre destinatari există
  // angajați fără niciun FP încă (prima atribuire). FP e decuplat de IIG/ILM.
  const [showFpModal, setShowFpModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  // Search după numele cursului — combinat cu typeFilter (AND).
  const [courseNameFilter, setCourseNameFilter] = useState('');
  // După atribuire reușită, stepper-ul rămâne complet verde (toți pașii bifați)
  // ca semnal vizual că s-a încheiat fluxul. Se resetează la următoarea selecție.
  const [justAssigned, setJustAssigned] = useState(false);

  const [companyDepartments, setCompanyDepartments] = useState([]);

  const [nameFilter, setNameFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [jobTitleFilter, setJobTitleFilter] = useState('');
  const [onlyEPI, setOnlyEPI] = useState(false);
  const [onlyIPDue, setOnlyIPDue] = useState(false);
  const [ipDueData, setIpDueData] = useState([]); // date scadență per angajat

  const [userRole, setUserRole] = useState('');
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [assignResult, setAssignResult] = useState(null); // Modal rezultat atribuire

  // Preview înainte de trimitere: SSM-istul vede destinatarii + cine va semna fișa fiecăruia și
  // poate corecta (scoate un destinatar greșit) ÎNAINTE să trimită. Nimic nu se creează până la
  // „Confirmă și trimite".
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // true când popup-ul CLM a fost deschis DIN preview (ca să ne întoarcem la preview, nu să închidem tot).
  const [clmFromPreview, setClmFromPreview] = useState(false);

  // Desemnarea ADMINULUI care semnează (per firmă). Gol = oricare admin autorizat (implicit).
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminCandidates, setAdminCandidates] = useState([]);
  const [adminCandidatesLoading, setAdminCandidatesLoading] = useState(false);
  const [adminSelections, setAdminSelections] = useState([]);

  // CLM — datele firmei pentru popup-ul de desemnare a conducătorilor la atribuire.
  // Disponibile doar pentru SSM (care administrează CLM); pentru COMPANY_ADMIN rămâne null → fără popup,
  // default „oricare CLM activ semnează".
  const [clmData, setClmData] = useState(null);
  const [showClmModal, setShowClmModal] = useState(false);
  const [clmSelections, setClmSelections] = useState({}); // { [deptKey]: [userId...] }
  const [clmScopeByGroup, setClmScopeByGroup] = useState({}); // { [deptKey]: departmentId | '__all__' }

  // Detectare overflow real pe lista de angajați — evită hint-ul fals când toți încap.
  const employeeListRef = useRef(null);
  const [listOverflows, setListOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = employeeListRef.current;
    if (!el) {
      setListOverflows(false);
      return;
    }
    const measure = () => {
      setListOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  });

  const typeOptions = [
    { value: '', label: t('assign.allTypes') },
    { value: 'IIG_SSM', label: t('courses.typeShort.IIG_SSM') },
    { value: 'ILM_SSM', label: t('courses.typeShort.ILM_SSM') },
    { value: 'IP_SSM', label: t('courses.typeShort.IP_SSM') },
    { value: 'SUPLIMENTARE_SSM', label: t('courses.categories.suplimentare') },
    { value: 'IIG_SU', label: t('courses.typeShort.IIG_SU') },
    { value: 'ILM_SU', label: t('courses.typeShort.ILM_SU') },
    { value: 'IP_SU', label: t('courses.typeShort.IP_SU') },
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (scopedCompanyId && courses.length > 0 && !employees.length) {
      handleCompanySelect(scopedCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedCompanyId, courses.length]);

  // Preselecție din URL: declanșează handleCompanySelect imediat ce avem datele inițiale.
  useEffect(() => {
    if (!preselectCompanyId || scopedCompanyId) return;
    if (courses.length === 0) return;
    if (employees.length > 0) return; // deja încărcați
    handleCompanySelect(preselectCompanyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectCompanyId, courses.length]);

  // După ce angajații sunt încărcați, preselectează angajatul din URL.
  useEffect(() => {
    if (!preselectEmployeeId) return;
    if (!employees.some((e) => e.id === preselectEmployeeId)) return;
    if (selectedEmployees.includes(preselectEmployeeId)) return;
    setSelectedEmployees([preselectEmployeeId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectEmployeeId, employees]);

  const fetchInitialData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserRole(user.role);

      const coursesRes = await coursesAPI.getAll();
      setCourses(coursesRes.data);

      if (['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user.role)) {
        const companiesRes = await api.get('/companies');
        setCompanies(companiesRes.data);
      } else if (
        ['COMPANY_ADMIN', 'DIRECTOR_GENERAL'].includes(user.role) ||
        // CLM = EMPLOYEE cu managesDepartment — trebuie să vadă angajații din
        // departamentele pe care le conduce. Filtrarea se face mai jos (isCLMOnly).
        (user.role === 'EMPLOYEE' && user.managesDepartment)
      ) {
        // Încarcă și departamentele propriei firme ca să putem filtra pe managerId.
        const [empRes, ipDueRes, deptRes] = await Promise.all([
          api.get('/employees'),
          api.get('/employees/ip-due?days=60').catch(() => ({ data: [] })),
          user.companyId
            ? departmentsAPI.getAll(user.companyId).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);
        setEmployees(empRes.data);
        setIpDueData(ipDueRes.data || []);
        setCompanyDepartments(deptRes.data || []);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      showToast(t('assign.loadError') + ': ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleCompanySelect = async (companyId) => {
    setSelectedCompany(companyId);
    setSelectedEmployees([]);
    setEmployees([]);
    setCompanyDepartments([]);
    setExistingAssignments([]);
    setDuplicateWarnings([]);
    setClmData(null);
    setNameFilter('');
    setDepartmentFilter('');
    setJobTitleFilter('');

    if (!companyId) return;

    // Păstrăm selecția de cursuri la schimbarea firmei — dacă un curs nu e compatibil
    // cu noile servicii, rămâne bifat invizibil (banner informativ deasupra listei)
    // și oricum backend-ul skip-uie atribuirea. Resetăm doar typeFilter-ul dacă a rămas
    // pe un serviciu indisponibil, ca lista să nu apară goală fără motiv clar.
    const nextCompanyObj = companies.find((c) => c.id === companyId);
    if (nextCompanyObj) {
      const serviceOk = (type) => {
        if (type?.endsWith('_SSM')) return nextCompanyObj.offersSSM !== false;
        if (type?.endsWith('_SU')) return nextCompanyObj.offersSU !== false;
        return true;
      };
      if (typeFilter && !serviceOk(typeFilter)) {
        setTypeFilter('');
      }
    }

    setLoadingEmployees(true);
    try {
      const [res, deptRes, ipDueRes] = await Promise.all([
        api.get(`/employees?companyId=${companyId}`),
        departmentsAPI.getAll(companyId),
        api.get('/employees/ip-due?days=60').catch(() => ({ data: [] })),
      ]);
      setEmployees(res.data);
      setCompanyDepartments(deptRes.data);
      setIpDueData(ipDueRes.data || []);

      // CLM pentru popup-ul de desemnare — best-effort (403 pentru roluri fără drept → null, fără popup).
      clmAPI
        .getCompanyClm(companyId)
        .then((r) => setClmData(r.data))
        .catch(() => setClmData(null));

      let assignments = [];
      try {
        const assignRes = await api.get(`/assignments?companyId=${companyId}`);
        assignments = assignRes.data || [];
        setExistingAssignments(assignments);
      } catch (e) {
        setExistingAssignments([]);
      }

      // Pre-check employees already assigned to ALL selected courses
      if (selectedCourses.length > 0 && assignments.length > 0) {
        const preSelected = res.data
          .filter((emp) =>
            selectedCourses.every((cId) =>
              assignments.some((a) => a.employeeId === emp.id && a.courseId === cId)
            )
          )
          .map((emp) => emp.id);
        setSelectedEmployees(preSelected);
        calculateDuplicates(preSelected, selectedCourses, assignments);
      }
    } catch (err) {
      showToast(t('assign.loadEmployeesError'), 'error');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const isAlreadyAssigned = (employeeId, courseId) => {
    return existingAssignments.some((a) => a.employeeId === employeeId && a.courseId === courseId);
  };

  // Când se selectează angajați sau cursuri, calculează warning-urile
  const calculateDuplicates = (empIds, courseIds, assignmentsOverride) => {
    const assigns = assignmentsOverride || existingAssignments;
    const warnings = [];
    empIds.forEach((empId) => {
      courseIds.forEach((courseId) => {
        if (assigns.some((a) => a.employeeId === empId && a.courseId === courseId)) {
          const emp = employees.find((e) => e.id === empId);
          const course = courses.find((c) => c.id === courseId);
          if (emp && course) {
            warnings.push(`${emp.user.firstName} ${emp.user.lastName} — ${course.title}`);
          }
        }
      });
    });
    setDuplicateWarnings(warnings);
  };

  const toggleCourse = (courseId) => {
    if (justAssigned) setJustAssigned(false);
    const next = selectedCourses.includes(courseId)
      ? selectedCourses.filter((id) => id !== courseId)
      : [...selectedCourses, courseId];
    setSelectedCourses(next);
    // Nu mai modificăm automat selectedEmployees — user-ul își gestionează angajații singur.
    calculateDuplicates(selectedEmployees, next);
  };

  const toggleEmployee = (empId) => {
    if (justAssigned) setJustAssigned(false);
    const next = selectedEmployees.includes(empId)
      ? selectedEmployees.filter((id) => id !== empId)
      : [...selectedEmployees, empId];
    setSelectedEmployees(next);
    calculateDuplicates(next, selectedCourses);
  };

  // Filtrare pe tipul permis în funcție de rolul curent + bife + statut CLM
  const allowedTypes = getCreatableCourseTypes(user, !!user?.managesDepartment);
  let baseCoursesAllowed = courses.filter((c) => allowedTypes.has(c.type));

  // Determină serviciile oferite de firma-țintă:
  //  - Pentru SSM roles: din lista `companies` pe baza `selectedCompany`
  //  - Pentru COMPANY_ADMIN / DIRECTOR_GENERAL / EMPLOYEE: din user-ul logat (propria firmă)
  const isSSMRole = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role);
  const selectedCompanyObj = companies.find((c) => c.id === selectedCompany);
  const effectiveServices = selectedCompanyObj
    ? { offersSSM: selectedCompanyObj.offersSSM, offersSU: selectedCompanyObj.offersSU }
    : !isSSMRole
      ? { offersSSM: user?.offersSSM, offersSU: user?.offersSU }
      : null;

  // Filtrare pe baza serviciilor: dacă firma nu oferă SU, ascundem cursurile *_SU. La fel pentru SSM.
  if (effectiveServices) {
    baseCoursesAllowed = baseCoursesAllowed.filter((c) => {
      if (c.type?.endsWith('_SSM')) return effectiveServices.offersSSM !== false;
      if (c.type?.endsWith('_SU')) return effectiveServices.offersSU !== false;
      return true;
    });
  }

  const courseNameQuery = courseNameFilter.trim().toLowerCase();
  const filteredCourses = baseCoursesAllowed.filter((c) => {
    if (typeFilter && c.type !== typeFilter) return false;
    if (courseNameQuery && !c.title.toLowerCase().includes(courseNameQuery)) return false;
    return true;
  });

  // Filtrul de tip — doar tipurile permise + oferite de firma-țintă
  const visibleTypeOptions = [
    { value: '', label: t('assign.allTypes') },
    ...typeOptions.slice(1).filter((o) => {
      if (!allowedTypes.has(o.value)) return false;
      if (effectiveServices) {
        if (o.value.endsWith('_SSM') && effectiveServices.offersSSM === false) return false;
        if (o.value.endsWith('_SU') && effectiveServices.offersSU === false) return false;
      }
      return true;
    }),
  ];

  const jobTitles = [...new Set(employees.map((e) => e.jobTitle).filter(Boolean))].sort();

  // Apply name / department / jobTitle filters
  // Pentru CLM (EMPLOYEE care e managerId pe dept) restrânge la angajații din dept-urile lui
  const isCLMOnly = user?.role === 'EMPLOYEE' && user?.managesDepartment;
  const myManagedDeptIds = new Set(
    isCLMOnly ? companyDepartments.filter((d) => d.managerId === user.id).map((d) => d.id) : []
  );

  const filteredEmployees = employees.filter((emp) => {
    if (isCLMOnly) {
      if (!emp.departmentId || !myManagedDeptIds.has(emp.departmentId)) return false;
    }
    const fullName = `${emp.user.firstName} ${emp.user.lastName}`.toLowerCase();
    if (nameFilter && !fullName.includes(nameFilter.toLowerCase())) return false;
    if (departmentFilter && emp.departmentId !== departmentFilter) return false;
    if (jobTitleFilter && emp.jobTitle !== jobTitleFilter) return false;
    if (onlyEPI && !emp.hasEPI) return false;
    if (onlyIPDue) {
      const ipInfo = ipDueData.find((d) => d.id === emp.id);
      if (!ipInfo) return false;
    }
    return true;
  });

  // Selecteaza tot departamentul
  const selectDepartment = (deptId) => {
    setDepartmentFilter(deptId);
    setJobTitleFilter('');
    if (deptId) {
      const deptEmpIds = employees.filter((e) => e.departmentId === deptId).map((e) => e.id);
      setSelectedEmployees((prev) => [...new Set([...prev, ...deptEmpIds])]);
      calculateDuplicates([...new Set([...selectedEmployees, ...deptEmpIds])], selectedCourses);
    }
  };

  const selectAllCourses = () => {
    // Union peste filtrul curent (la fel ca la angajați): „selectează tot" ADAUGĂ cursurile
    // filtrate acum la selecția existentă, nu o înlocuiește; „deselectează tot" scoate DOAR
    // cursurile vizibile acum. Așa selecția supraviețuiește schimbării filtrului tip/nume.
    const allFilteredIds = filteredCourses.map((c) => c.id);
    const allSelected =
      allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedCourses.includes(id));
    const next = allSelected
      ? selectedCourses.filter((id) => !allFilteredIds.includes(id))
      : [...new Set([...selectedCourses, ...allFilteredIds])];
    setSelectedCourses(next);
    calculateDuplicates(selectedEmployees, next);
  };

  const selectAllEmployees = () => {
    const allFilteredIds = filteredEmployees.map((e) => e.id);
    const allSelected = allFilteredIds.every((id) => selectedEmployees.includes(id));
    const next = allSelected
      ? selectedEmployees.filter((id) => !allFilteredIds.includes(id))
      : [...new Set([...selectedEmployees, ...allFilteredIds])];
    setSelectedEmployees(next);
    calculateDuplicates(next, selectedCourses);
  };

  // ── Desemnarea CLM la atribuire ──
  // Doar tipurile cu slot de conducător (ILM/IP/SUPLIMENTARE) declanșează popup-ul. IIG nu are CLM.
  const CLM_RELEVANT = [
    'ILM_SSM',
    'ILM_SU',
    'IP_SSM',
    'IP_SU',
    'SUPLIMENTARE_SSM',
    'SUPLIMENTARE_SU',
  ];
  const selectedCoursesData = courses.filter((c) => selectedCourses.includes(c.id));
  const hasClmRelevant = selectedCoursesData.some((c) => CLM_RELEVANT.includes(c.type));

  const activeMandates = (arr) => (arr || []).filter((m) => m.status === 'ACTIVE');
  const companyWideClm = clmData ? activeMandates(clmData.companyWide) : [];
  const dedupByUser = (arr) => {
    const seen = new Set();
    const out = [];
    for (const m of arr) {
      if (!seen.has(m.userId)) {
        seen.add(m.userId);
        out.push(m);
      }
    }
    return out;
  };
  // CLM-ii STRICT ai unui departament (doar mandatele pe acel dept — fără cele „pe toată firma").
  // Pentru filtrarea pe scope: când alegi un departament, vezi DOAR conducătorii acelui departament.
  const deptStrictClm = (deptId) => {
    if (!clmData || !deptId) return [];
    const deptM = clmData.departments.find((d) => d.id === deptId)?.mandates || [];
    return dedupByUser(activeMandates(deptM));
  };
  // Toți CLM-ii activi din firmă (orice departament + toată firma) — pentru scope-ul „Toată firma".
  const allActiveClm = clmData
    ? dedupByUser([
        ...(clmData.departments || []).flatMap((d) => activeMandates(d.mandates)),
        ...companyWideClm,
      ])
    : [];
  // CLM-ii pentru un scope: un departament anume (STRICT), sau '__all__' = toți din firmă.
  const eligibleForScope = (scope) => (scope === '__all__' ? allActiveClm : deptStrictClm(scope));
  // Opțiunile barei de scope (search): departamentele firmei + „Toată firma".
  const clmScopeOptions = [
    ...(clmData?.departments || []).map((d) => ({ value: d.id, label: d.name })),
    { value: '__all__', label: t('clm.modal.wholeCompany') },
  ];

  // Angajații selectați, grupați pe departament (cheia '__none__' pentru cei fără departament).
  const clmDeptGroups = (() => {
    const map = new Map();
    employees
      .filter((e) => selectedEmployees.includes(e.id))
      .forEach((e) => {
        const key = e.departmentId || '__none__';
        if (!map.has(key))
          map.set(key, {
            key,
            deptId: e.departmentId || null,
            deptName: e.department?.name || null,
            count: 0,
          });
        map.get(key).count++;
      });
    return [...map.values()];
  })();

  const shouldChooseClm = () => isSSMUser && hasClmRelevant && clmData && allActiveClm.length > 0;

  const groupDefaultScope = (g) => g.deptId || '__all__';

  // userId-urile angajaților INSTRUIȚI dintr-un grup (departament). Un CLM care e el însuși printre
  // cei instruiți în grup NU se pre-bifează: nu-și poate semna propria fișă (acolo adminul preia
  // verificarea). Rămâne bifabil manual dacă vrei să-l desemnezi pentru COLEGII din grup.
  const traineeUserIdsInGroup = (g) =>
    new Set(
      employees
        .filter((e) => selectedEmployees.includes(e.id) && (e.departmentId || '__none__') === g.key)
        .map((e) => e.user?.id)
        .filter(Boolean)
    );
  // Pre-selecția implicită de CLM pentru un grup+scope: toți eligibilii MINUS cei instruiți în grup.
  const defaultClmSelection = (g, scope) =>
    eligibleForScope(scope)
      .map((m) => m.userId)
      .filter((uid) => !traineeUserIdsInGroup(g).has(uid));

  const openClmModal = () => {
    const initSel = {};
    const initScope = {};
    clmDeptGroups.forEach((g) => {
      const scope = groupDefaultScope(g);
      initScope[g.key] = scope;
      initSel[g.key] = defaultClmSelection(g, scope);
    });
    setClmScopeByGroup(initScope);
    setClmSelections(initSel);
    setShowClmModal(true);
  };

  // Schimbarea scope-ului (departament / toată firma) reîncarcă lista de CLM și pre-bifează toți.
  const changeGroupScope = (g, scope) => {
    setClmScopeByGroup((prev) => ({ ...prev, [g.key]: scope }));
    setClmSelections((prev) => ({ ...prev, [g.key]: defaultClmSelection(g, scope) }));
  };

  const toggleClm = (groupKey, userId) => {
    setClmSelections((prev) => {
      const cur = prev[groupKey] || [];
      const next = cur.includes(userId) ? cur.filter((id) => id !== userId) : [...cur, userId];
      return { ...prev, [groupKey]: next };
    });
  };
  const selectAllInGroup = (g) => {
    const scope = clmScopeByGroup[g.key] || groupDefaultScope(g);
    setClmSelections((prev) => ({ ...prev, [g.key]: defaultClmSelection(g, scope) }));
  };
  const clearGroup = (g) => setClmSelections((prev) => ({ ...prev, [g.key]: [] }));
  const selectAllClmEverywhere = () => {
    const all = {};
    clmDeptGroups.forEach((g) => {
      const scope = clmScopeByGroup[g.key] || groupDefaultScope(g);
      all[g.key] = defaultClmSelection(g, scope);
    });
    setClmSelections(all);
  };

  // Construiește harta clmDesignations pentru backend = selecția EXPLICITĂ per departament (cei bifați).
  // Predictibil: cine e bifat, ăla semnează (backend tratează desemnarea ca override, cu fallback pe
  // CLM-ii care acoperă angajatul dacă toți cei aleși devin inactivi).
  const buildClmDesignations = () => {
    if (!shouldChooseClm()) return undefined;
    const out = {};
    clmDeptGroups.forEach((g) => {
      const sel = clmSelections[g.key] || [];
      if (sel.length > 0) out[g.deptId || '__none__'] = sel;
    });
    return Object.keys(out).length ? out : undefined;
  };

  // Harta adminDesignations pentru backend = { [companyId]: [userId...] }. Gol → undefined (oricare admin).
  const buildAdminDesignations = () => {
    const cid = isSSMUser ? selectedCompany : user?.companyId;
    if (!cid || adminSelections.length === 0) return undefined;
    return { [cid]: adminSelections };
  };

  // Cere backend-ului previzualizarea (dry-run): cine sunt destinatarii și cine va semna fișa
  // fiecăruia, fără să creeze nimic. empIds explicit ca să evităm state-ul învechit la re-fetch.
  const fetchPreview = async (empIds) => {
    setPreviewLoading(true);
    try {
      const res = await assignmentsAPI.preview({
        courseIds: selectedCourses,
        employeeIds: empIds,
        clmDesignations: buildClmDesignations(),
        adminDesignations: buildAdminDesignations(),
      });
      setPreviewData(res.data);
    } catch (err) {
      showToast(err.response?.data?.error || t('assign.previewError'), 'error');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = async () => {
    setShowDuplicateConfirm(false);
    setShowClmModal(false);
    setClmFromPreview(false);
    setShowPreview(true);
    await fetchPreview(selectedEmployees);
  };

  // Redeschide popup-ul de desemnare CLM din preview, PĂSTRÂND alegerile curente (nu reinițializează
  // ca openClmModal). La confirmare → openPreview re-rezolvă; la anulare → ne întoarcem la preview.
  const editClmFromPreview = () => {
    setClmFromPreview(true);
    setShowPreview(false);
    setShowClmModal(true);
  };

  // Anulare popup CLM: dacă a fost deschis din preview, revenim la preview (fără re-fetch — păstrăm
  // datele); altfel (flux normal de atribuire) doar închidem.
  const cancelClmModal = () => {
    setShowClmModal(false);
    if (clmFromPreview) {
      setClmFromPreview(false);
      setShowPreview(true);
    }
  };

  // ── Desemnarea ADMINULUI din preview ──
  // Deschide selectorul de admini ai firmei-țintă. Doar din preview → anularea revine la preview.
  const openAdminModal = async () => {
    const cid = isSSMUser ? selectedCompany : user?.companyId;
    setShowPreview(false);
    setShowAdminModal(true);
    if (!cid) return;
    setAdminCandidatesLoading(true);
    try {
      const res = await assignmentsAPI.adminCandidates(cid);
      setAdminCandidates(res.data?.admins || []);
    } catch (err) {
      showToast(err.response?.data?.error || t('assign.preview.adminLoadError'), 'error');
      setAdminCandidates([]);
    } finally {
      setAdminCandidatesLoading(false);
    }
  };

  const confirmAdminModal = () => {
    setShowAdminModal(false);
    openPreview(); // re-rezolvă preview-ul cu noua desemnare de admin
  };

  const cancelAdminModal = () => {
    setShowAdminModal(false);
    setShowPreview(true);
  };

  const toggleAdminSel = (uid) => {
    setAdminSelections((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  // Scoate un destinatar din lot direct din preview (corecție „am greșit cui trimit") și
  // re-rezolvă semnatarii pentru lista rămasă. Nimic nu e încă trimis.
  const removeRecipientFromPreview = async (employeeId) => {
    const next = selectedEmployees.filter((id) => id !== employeeId);
    setSelectedEmployees(next);
    calculateDuplicates(next, selectedCourses);
    if (next.length === 0) {
      setPreviewData(null);
      return;
    }
    await fetchPreview(next);
  };

  // Decide pasul următor după rezolvarea duplicatelor: popup CLM (dacă e cazul) → apoi preview.
  const proceedToAssign = () => {
    setShowDuplicateConfirm(false);
    if (shouldChooseClm()) openClmModal();
    else openPreview();
  };

  const handleAssign = async () => {
    if (selectedCourses.length === 0 || selectedEmployees.length === 0) {
      showToast(t('assign.selectAtLeastOneWarning'), 'warning');
      return;
    }
    if (duplicateWarnings.length > 0) {
      setShowDuplicateConfirm(true);
      return;
    }
    proceedToAssign();
  };

  // Confirmarea reviewului: dacă printre destinatari sunt angajați fără FP (prima atribuire), întrebăm
  // explicit prin popup dacă trimitem și fișele postului; altfel atribuim direct, fără FP.
  const confirmPreview = () => {
    if (previewData?.summary?.needJobDescription > 0) {
      setShowPreview(false);
      setShowFpModal(true);
    } else {
      doAssign(false);
    }
  };

  const doAssign = async (sendJobDescriptions = false) => {
    setShowDuplicateConfirm(false);
    setShowClmModal(false);
    setShowPreview(false);
    setShowFpModal(false);
    setLoading(true);
    try {
      const result = await assignmentsAPI.bulk({
        courseIds: selectedCourses,
        employeeIds: selectedEmployees,
        dueDate: dueDate || null,
        skipDuplicates: true,
        sendJobDescriptions,
        clmDesignations: buildClmDesignations(),
        adminDesignations: buildAdminDesignations(),
      });

      setAssignResult(result.data);

      const created = result.data?.created ?? selectedCourses.length * selectedEmployees.length;
      const skipped = result.data?.skipped ?? duplicateWarnings.length;
      let msg = t('assign.createdSuccess', { count: created });
      if (skipped > 0) msg += ' ' + t('assign.skippedSuffix', { count: skipped });
      showToast(msg, 'success');

      setSelectedCourses([]);
      setSelectedEmployees([]);
      setDueDate('');
      setDuplicateWarnings([]);
      // Stepper rămâne complet verde până la următoarea selecție — semnal vizual că fluxul s-a încheiat.
      setJustAssigned(true);
    } catch (err) {
      const errMsg = err.response?.data?.error || t('assign.assignError');
      setAssignResult({ error: errMsg });
      showToast(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const isSSMUser = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(userRole);
  const showCompanyStep = isSSMUser && !embedded;
  const canAssign =
    selectedCourses.length > 0 &&
    selectedEmployees.length > 0 &&
    (isSSMUser ? selectedCompany : true);

  const steps = showCompanyStep
    ? [
        { label: t('assign.step1') },
        { label: t('assign.step2') },
        { label: t('assign.step3') },
        { label: t('assign.step4') },
      ]
    : [{ label: t('assign.step1') }, { label: t('assign.step3') }, { label: t('assign.step4') }];

  const currentStep = showCompanyStep
    ? selectedCourses.length === 0
      ? 0
      : !selectedCompany
        ? 1
        : selectedEmployees.length === 0
          ? 2
          : 3
    : selectedCourses.length === 0
      ? 0
      : selectedEmployees.length === 0
        ? 1
        : 2;

  // Dacă tocmai am atribuit cu succes, stepper-ul rămâne complet (toți pașii verzi) până la
  // prima nouă selecție. StepIndicator marchează „isCompleted" pentru orice idx < currentStep,
  // deci trecem currentStep = steps.length să bifeze toți pașii.
  const effectiveStep = justAssigned ? steps.length : currentStep;

  // Tour contextual: 2 stepuri pe selecție rapidă + due date.
  // Doar când e pagina standalone (embedded=false), ca să nu apară în CompanyDetails embedded.
  const assignTourSteps = [
    {
      selector: '[data-tour="assign-select"]',
      titleKey: 'onboarding.tour.assign.select.title',
      descKey: 'onboarding.tour.assign.select.desc',
      placement: 'bottom',
    },
    {
      selector: '[data-tour="assign-due"]',
      titleKey: 'onboarding.tour.assign.due.title',
      descKey: 'onboarding.tour.assign.due.desc',
      placement: 'top',
    },
  ];
  const { tour: assignTour, startTour: startAssignTour } = usePageTour({
    tourId: 'assign-courses',
    steps: assignTourSteps,
    autoStart: !embedded,
  });

  // Etichete pentru rolurile de semnatar întoarse de preview (backend dă codul, UI traduce).
  const signerRoleLabel = (role) =>
    ({
      EMPLOYEE: t('assign.preview.roleEmployee'),
      SSM: t('assign.preview.roleSSM'),
      CADRU_PSI: t('assign.preview.rolePSI'),
      MANAGER: t('assign.preview.roleManager'),
      ADMIN: t('assign.preview.roleAdmin'),
      DIRECTOR: t('assign.preview.roleDirector'),
    })[role] || role;

  // Afișează persoanele unui slot: una anume, „oricare dintre…", sau avertisment dacă lipsește.
  const renderSlotPeople = (slot) => {
    if (slot.missing || slot.people.length === 0) {
      return (
        <span className="text-danger-600 dark:text-danger-400 italic">
          {t('assign.preview.noSigner')}
        </span>
      );
    }
    const names = slot.people
      .map((p) => p.name)
      .filter(Boolean)
      .join(', ');
    return (
      <span className="text-slate-700 dark:text-slate-200">
        {slot.pool ? t('assign.preview.anyOf', { names }) : names}
        {slot.note === 'OR_EMPOWERED' && (
          <span className="text-slate-400 dark:text-slate-500">
            {' '}
            {t('assign.preview.orEmpowered')}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {!embedded && assignTour}
      {!embedded && (
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              {t('assign.title')}
              <HelpTooltip section="assign-courses" text={t('assign.helpTooltip')} />
            </span>
          }
          subtitle={t('assign.subtitle')}
          actions={
            <Button variant="ghost" size="sm" onClick={startAssignTour}>
              {t('onboarding.replay.btn')}
            </Button>
          }
        />
      )}

      <Card data-tour="assign-select">
        <StepIndicator steps={steps} currentStep={effectiveStep} />
        {/* După o atribuire reușită: buton explicit de reset, pentru cazul în care userul
            vrea să atribuie alt curs altor angajați și nu vrea să se bazeze pe reset-ul automat
            la primul toggle. */}
        {justAssigned && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg bg-accent-50 dark:bg-accent-950/30 border border-accent-200 dark:border-accent-800">
            <p className="text-sm text-accent-800 dark:text-accent-300 font-medium">
              {t('assign.assignmentDone')}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setJustAssigned(false);
                setSelectedCourses([]);
                setSelectedEmployees([]);
                setDueDate('');
                setDuplicateWarnings([]);
                setCourseNameFilter('');
                setTypeFilter('');
              }}
            >
              {t('assign.startNewAssignment')}
            </Button>
          </div>
        )}
      </Card>

      {/* Duplicate warnings */}
      {duplicateWarnings.length > 0 && (
        <Alert type="warning">
          <p className="font-semibold text-warning-900 dark:text-warning-300 mb-2">
            {t('assign.duplicatesExistAlert', { count: duplicateWarnings.length })}
          </p>
          <ul className="text-sm text-warning-800 dark:text-warning-300 space-y-0.5">
            {duplicateWarnings.slice(0, 5).map((w, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-warning-500 flex-shrink-0" />
                {w}
              </li>
            ))}
            {duplicateWarnings.length > 5 && (
              <li className="text-warning-600 dark:text-warning-300 ml-2.5">
                {t('assign.duplicatesMore', { count: duplicateWarnings.length - 5 })}
              </li>
            )}
          </ul>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COLUMN 1: Courses + Company + Deadline */}
        <div className="space-y-4">
          {/* Courses */}
          <Card>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1">
                <StepNumber number={1} />
                {t('assign.trainingsTitle')}
                <HelpTooltip text={t('common.glossaryAbbr')} size={14} />
              </h2>
              {filteredCourses.length > 0 && (
                <Button variant="ghost" size="sm" onClick={selectAllCourses}>
                  {filteredCourses.every((c) => selectedCourses.includes(c.id))
                    ? t('assign.deselectAllShort')
                    : t('assign.selectAll')}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="select w-full"
              >
                {visibleTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={courseNameFilter}
                onChange={(e) => setCourseNameFilter(e.target.value)}
                placeholder={t('assign.searchCourseName')}
                className="input w-full"
              />
            </div>

            {filteredCourses.length === 0 ? (
              (() => {
                // #20: niciun curs creat în tot catalogul → mesaj clar + buton spre crearea cursurilor.
                if (courses.length === 0) {
                  return (
                    <EmptyState
                      icon={<BookOpenIcon />}
                      title={t('assign.noCoursesCreated')}
                      description={t('assign.noCoursesCreatedDesc')}
                      actionLabel={t('assign.goCreateCourse')}
                      onAction={() => navigate('/courses')}
                    />
                  );
                }
                // Decide mesajul: lipsă de servicii pe firmă vs. lipsă cursuri în catalog
                const firmLabel = selectedCompanyObj?.name || t('assign.defaultFirmLabel');
                let title = t('assign.noTrainings');
                let description = typeFilter
                  ? t('assign.noCoursesForType')
                  : t('assign.noCoursesAvail');
                if (effectiveServices) {
                  const typeService = typeFilter?.endsWith('_SSM')
                    ? 'SSM'
                    : typeFilter?.endsWith('_SU')
                      ? 'SU'
                      : null;
                  const missingSSM = effectiveServices.offersSSM === false;
                  const missingSU = effectiveServices.offersSU === false;
                  if (typeService === 'SSM' && missingSSM) {
                    title = t('assign.noSSMForCompany', { company: firmLabel });
                    description = t('assign.switchTypeOrActivateSSM');
                  } else if (typeService === 'SU' && missingSU) {
                    title = t('assign.noSUForCompany', { company: firmLabel });
                    description = t('assign.switchTypeOrActivateSU');
                  } else if (!typeFilter && (missingSSM || missingSU)) {
                    const missing =
                      missingSSM && missingSU
                        ? t('assign.missingServiceBoth')
                        : missingSSM
                          ? t('assign.missingServiceSSM')
                          : t('assign.missingServiceSU');
                    description =
                      missingSSM && missingSU
                        ? t('assign.missingServiceDescBoth', { company: firmLabel, missing })
                        : t('assign.missingServiceDescPartial', { company: firmLabel, missing });
                  }
                }
                return (
                  <EmptyState icon={<BookOpenIcon />} title={title} description={description} />
                );
              })()
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredCourses.map((course) => (
                  <label
                    key={course.id}
                    className={`flex items-center gap-2.5 p-2.5 border rounded-lg cursor-pointer transition-all ${
                      selectedCourses.includes(course.id)
                        ? 'bg-primary-50 dark:bg-primary-950/50 border-primary-300 dark:border-primary-700'
                        : 'border-slate-200 dark:border-slate-800 hover:bg-surface-secondary'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course.id)}
                      onChange={() => toggleCourse(course.id)}
                      className="h-4 w-4 text-primary-600 rounded border-slate-300 dark:border-slate-700"
                    />
                    <span className="text-sm text-slate-800 dark:text-slate-100">
                      {course.title}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {selectedCourses.length > 0 && (
              <div className="mt-3 p-2.5 bg-primary-50 dark:bg-primary-950/50 rounded-lg flex items-center gap-2">
                <CheckIcon />
                <p className="text-xs text-primary-700 dark:text-primary-300 font-medium">
                  {selectedCourses.length > 1
                    ? t('assign.trainingsSelectedPlural', { count: selectedCourses.length })
                    : t('assign.trainingsSelected', { count: selectedCourses.length })}
                </p>
              </div>
            )}
          </Card>

          {/* Company (SSM users only, not embedded) */}
          {showCompanyStep && (
            <Card>
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center mb-3">
                <StepNumber number={2} />
                {t('assign.clientCompany')}
              </h2>
              {/* SearchableSelect — userul poate căuta firma după nume când sunt multe în portofoliu,
                  în loc să dea scroll lung pe un select clasic. */}
              <SearchableSelect
                value={selectedCompany}
                onChange={handleCompanySelect}
                placeholder={t('assign.chooseCompany')}
                options={companies.map((c) => ({ value: c.id, label: c.name }))}
              />

              {selectedCompany && (
                <div className="mt-3 p-3 bg-accent-50 dark:bg-accent-950/50 rounded-lg flex items-center gap-2.5">
                  <BuildingIcon />
                  <div className="flex-1">
                    <p className="text-xs text-accent-700 dark:text-accent-300 font-medium">
                      {companies.find((c) => c.id === selectedCompany)?.name}
                    </p>
                    <p className="text-xs text-accent-600 dark:text-accent-300 mt-0.5">
                      {employees.length !== 1
                        ? t('assign.employeesAvailablePlural', { count: employees.length })
                        : t('assign.employeesAvailable', { count: employees.length })}
                    </p>
                  </div>
                  {selectedCompanyObj &&
                    (selectedCompanyObj.offersSSM === false ||
                      selectedCompanyObj.offersSU === false) && (
                      <div className="text-[10px] text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-slate-800 border border-amber-200 dark:border-slate-700 rounded px-1.5 py-0.5">
                        {selectedCompanyObj.offersSSM === false && t('assign.companyMissingSSM')}
                        {selectedCompanyObj.offersSSM === false &&
                          selectedCompanyObj.offersSU === false &&
                          ' • '}
                        {selectedCompanyObj.offersSU === false && t('assign.companyMissingSU')}
                      </div>
                    )}
                </div>
              )}
            </Card>
          )}

          {/* Deadline */}
          <Card data-tour="assign-due">
            <h2 className="text-base font-semibold text-slate-900 flex items-center mb-3">
              <StepNumber number={showCompanyStep ? 3 : 2} />
              {t('assign.deadlineOptional')}
            </h2>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </Card>
        </div>

        {/* COLUMN 2-3: Employees */}
        <Card className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
            <h2 className="text-base font-semibold text-slate-900 flex items-center">
              <StepNumber number={showCompanyStep ? 4 : 3} />
              {t('assign.employeesTitle')}
              {selectedEmployees.length > 0 && (
                <Badge color="blue" className="ml-2">
                  {selectedEmployees.length}{' '}
                  {selectedEmployees.length > 1 ? t('assign.selectedPlural') : t('assign.selected')}
                </Badge>
              )}
            </h2>
            <div className="flex items-center gap-2">
              {filteredEmployees.length > 0 && (
                <Button variant="ghost" size="sm" onClick={selectAllEmployees}>
                  {filteredEmployees.every((e) => selectedEmployees.includes(e.id))
                    ? t('assign.deselectAll')
                    : t('assign.selectAll')}
                </Button>
              )}
              {selectedEmployees.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedEmployees([]);
                    setDuplicateWarnings([]);
                  }}
                >
                  {t('assign.clearSelection')}
                </Button>
              )}
            </div>
          </div>

          {showCompanyStep && !selectedCompany && (
            <EmptyState
              icon={<BuildingEmptyIcon />}
              title={t('assign.selectCompany')}
              description={t('assign.selectCompanyHint')}
            />
          )}

          {loadingEmployees && <Spinner label={t('assign.loadingEmployees')} />}

          {!loadingEmployees && employees.length > 0 && (
            <>
              {/* Filtre */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                <input
                  type="text"
                  placeholder={t('assign.searchEmployee')}
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className="input"
                />
                <select
                  value={departmentFilter}
                  onChange={(e) => selectDepartment(e.target.value)}
                  className="select"
                >
                  <option value="">{t('assign.allDepartments')}</option>
                  {companyDepartments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d._count?.employees || 0})
                    </option>
                  ))}
                </select>
                <select
                  value={jobTitleFilter}
                  onChange={(e) => setJobTitleFilter(e.target.value)}
                  className="select"
                >
                  <option value="">{t('assign.allJobs')}</option>
                  {(departmentFilter
                    ? [
                        ...new Set(
                          employees
                            .filter((e) => e.departmentId === departmentFilter)
                            .map((e) => e.jobTitle)
                            .filter(Boolean)
                        ),
                      ].sort()
                    : jobTitles
                  ).map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </select>
              </div>

              {/* Toggle EPI + IP scadent + contor */}
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={onlyEPI}
                      onChange={(e) => setOnlyEPI(e.target.checked)}
                      className="h-4 w-4 text-danger-600 rounded"
                    />
                    <span className="text-xs font-medium text-danger-700 dark:text-danger-300">
                      {t('assign.onlyEPI')}
                    </span>
                  </label>
                  {ipDueData.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onlyIPDue}
                        onChange={(e) => setOnlyIPDue(e.target.checked)}
                        className="h-4 w-4 text-warning-600 rounded"
                      />
                      <span className="text-xs font-medium text-warning-700 dark:text-warning-300">
                        {t('assign.onlyIPDueCount', {
                          count: ipDueData.filter((d) => employees.some((e) => e.id === d.id))
                            .length,
                        })}
                      </span>
                    </label>
                  )}
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {t('assign.employeesShown', {
                    filtered: filteredEmployees.length,
                    total: employees.length,
                  })}
                </span>
              </div>

              {(nameFilter || departmentFilter || jobTitleFilter || onlyEPI || onlyIPDue) && (
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('assign.employeesShownSimple', {
                      filtered: filteredEmployees.length,
                      total: employees.length,
                    })}
                  </p>
                  <button
                    onClick={() => {
                      setNameFilter('');
                      setDepartmentFilter('');
                      setJobTitleFilter('');
                      setOnlyEPI(false);
                      setOnlyIPDue(false);
                    }}
                    className="text-xs text-danger-500 dark:text-danger-300 hover:text-danger-700 flex items-center gap-0.5"
                  >
                    <ClearFiltersIcon />
                    {t('common.clearFilters')}
                  </button>
                </div>
              )}

              {filteredEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-400 dark:text-slate-400">
                  <p className="text-sm">{t('assign.noEmployeesMatchFilters')}</p>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <div
                      ref={employeeListRef}
                      className="space-y-2 max-h-[560px] overflow-y-auto pr-2 custom-scrollbar"
                    >
                      {filteredEmployees.map((emp) => {
                        const alreadyAssigned =
                          selectedCourses.length > 0 &&
                          selectedCourses.every((cId) => isAlreadyAssigned(emp.id, cId));
                        const partiallyAssigned =
                          !alreadyAssigned &&
                          selectedCourses.some((cId) => isAlreadyAssigned(emp.id, cId));
                        const isSelected = selectedEmployees.includes(emp.id);
                        const ipInfo = ipDueData.find((d) => d.id === emp.id);
                        return (
                          <label
                            key={emp.id}
                            className={`flex items-center p-3 rounded-lg cursor-pointer border transition-all ${
                              isSelected
                                ? 'bg-primary-50 dark:bg-primary-950/50 border-primary-300 dark:border-primary-700'
                                : partiallyAssigned
                                  ? 'bg-warning-50 dark:bg-warning-950/50 border-warning-200 dark:border-warning-700 hover:border-warning-300'
                                  : 'bg-surface border-slate-200 dark:border-slate-800 hover:border-slate-300 hover:bg-surface-secondary'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleEmployee(emp.id)}
                              className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 dark:border-slate-700 rounded"
                            />
                            <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                              {emp.user.firstName[0]}
                              {emp.user.lastName[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-900 dark:text-slate-100 text-sm">
                                  {emp.user.firstName} {emp.user.lastName}
                                </span>
                                <RoleBadge role={emp.user.role} />
                                {emp.hasEPI && (
                                  <Badge color="red" className="text-[10px] font-bold">
                                    {t('employees.epi')}
                                  </Badge>
                                )}
                                {ipInfo?.ipSSM &&
                                  (ipInfo.ipSSM.neverTrained || !ipInfo.ipSSM.dueDate ? (
                                    <Badge color="red" className="text-[10px]">
                                      {t('assign.ssmNotTrained')}
                                    </Badge>
                                  ) : (
                                    <Badge
                                      color={
                                        Math.ceil(
                                          (new Date(ipInfo.ipSSM.dueDate) - new Date()) / 86400000
                                        ) <= 7
                                          ? 'red'
                                          : 'yellow'
                                      }
                                      className="text-[10px]"
                                    >
                                      {t('assign.ssmDaysShort', {
                                        days: Math.ceil(
                                          (new Date(ipInfo.ipSSM.dueDate) - new Date()) / 86400000
                                        ),
                                      })}
                                    </Badge>
                                  ))}
                                {ipInfo?.ipSU &&
                                  (ipInfo.ipSU.neverTrained || !ipInfo.ipSU.dueDate ? (
                                    <Badge color="red" className="text-[10px]">
                                      {t('assign.suNotTrained')}
                                    </Badge>
                                  ) : (
                                    <Badge
                                      color={
                                        Math.ceil(
                                          (new Date(ipInfo.ipSU.dueDate) - new Date()) / 86400000
                                        ) <= 7
                                          ? 'red'
                                          : 'yellow'
                                      }
                                      className="text-[10px]"
                                    >
                                      {t('assign.suDaysShort', {
                                        days: Math.ceil(
                                          (new Date(ipInfo.ipSU.dueDate) - new Date()) / 86400000
                                        ),
                                      })}
                                    </Badge>
                                  ))}
                                {alreadyAssigned && (
                                  <Badge color="green" className="flex items-center gap-1">
                                    <CheckIcon />
                                    {t('assign.alreadyAssigned')}
                                  </Badge>
                                )}
                                {partiallyAssigned && (
                                  <Badge color="yellow" className="flex items-center gap-1">
                                    <WarningIcon />
                                    {t('assign.partiallyAssigned')}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                                {[emp.jobTitle, emp.department?.name].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                            {isSelected && (
                              <span className="text-primary-500 dark:text-primary-300 ml-2 flex-shrink-0">
                                <CheckIcon />
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    {/* Fade-gradient jos — doar dacă lista chiar scrolează. */}
                    {listOverflows && (
                      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white via-white/70 to-transparent dark:from-slate-900 dark:via-slate-900/70" />
                    )}
                  </div>
                  {/* Hint scroll — doar când există overflow real, nu pe baza numărului de elemente. */}
                  {listOverflows && (
                    <div className="text-xs text-slate-600 dark:text-slate-300 font-medium text-center mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 text-primary-500 dark:text-primary-300 animate-bounce"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 14l-7 7m0 0l-7-7m7 7V3"
                        />
                      </svg>
                      {t('assign.scrollHint', { count: filteredEmployees.length })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {!loadingEmployees && (isSSMUser ? selectedCompany : true) && employees.length === 0 && (
            <EmptyState
              icon={<UserEmptyIcon />}
              title={t('assign.noEmployeesFound')}
              description={t('assign.noEmployeesAvailable')}
            />
          )}
        </Card>
      </div>

      {/* Summary + Action — sticky jos ca să fie mereu vizibil fără scroll */}
      {canAssign && (
        <div className="sticky bottom-0 z-20 -mx-4 sm:mx-0 pt-2">
          <Card
            className={`${duplicateWarnings.length > 0 ? 'bg-warning-50 dark:bg-warning-950/50 border-warning-200 dark:border-warning-700' : 'bg-primary-50 dark:bg-primary-950/50 border-primary-200 dark:border-primary-700'} shadow-lg border-2`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-lg text-slate-900 dark:text-slate-100">
                  {t('assign.assignmentSummary')}
                </p>
                <p className="text-sm mt-1 text-slate-600 dark:text-slate-300">
                  <strong>
                    {selectedCourses.length > 1
                      ? t('assign.coursesSelectedSummaryPlural', { count: selectedCourses.length })
                      : t('assign.coursesSelectedSummary', { count: selectedCourses.length })}
                  </strong>
                  {isSSMUser && selectedCompany && (
                    <>
                      {' '}
                      <span className="text-slate-400 dark:text-slate-400">-&gt;</span>{' '}
                      <strong>{companies.find((c) => c.id === selectedCompany)?.name}</strong>
                    </>
                  )}{' '}
                  <span className="text-slate-400 dark:text-slate-400">-&gt;</span>{' '}
                  <strong>
                    {selectedEmployees.length > 1
                      ? t('assign.employeesSelectedSummaryPlural', {
                          count: selectedEmployees.length,
                        })
                      : t('assign.employeesSelectedSummary', { count: selectedEmployees.length })}
                  </strong>
                  {dueDate && (
                    <>
                      {' '}
                      · {t('assign.assignDeadlineLabel')}:{' '}
                      <strong>{new Date(dueDate).toLocaleDateString('ro-RO')}</strong>
                    </>
                  )}
                  {duplicateWarnings.length > 0 && (
                    <>
                      {' '}
                      ·{' '}
                      <span className="text-warning-700 dark:text-warning-300 font-semibold">
                        {t('assign.duplicatesWillBeSkipped', { count: duplicateWarnings.length })}
                      </span>
                    </>
                  )}
                </p>
                {/* Listă cursuri selectate — vizibilă când sunt mai multe, ca să eviți greșelile */}
                {selectedCourses.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selectedCourses.map((cId) => {
                      const c =
                        filteredCourses.find((fc) => fc.id === cId) ||
                        courses.find((cc) => cc.id === cId);
                      if (!c) return null;
                      return (
                        <span
                          key={cId}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/60 dark:bg-slate-800/60 text-xs font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                        >
                          {c.title}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <Button
                variant={duplicateWarnings.length > 0 ? 'accent' : 'primary'}
                size="lg"
                onClick={handleAssign}
                loading={loading}
                className="flex-shrink-0"
              >
                {!loading && <SendIcon />}
                {loading ? t('assign.assigning') : t('assign.assignNow')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <ConfirmModal
        open={showDuplicateConfirm}
        onClose={() => setShowDuplicateConfirm(false)}
        onConfirm={proceedToAssign}
        title={t('assign.duplicatesConfirmTitle')}
        confirmLabel={t('assign.continueAnyway')}
        cancelLabel={t('common.cancel')}
        variant="accent"
        message={t('assign.duplicatesConfirmMessage', { count: duplicateWarnings.length })}
      />

      {/* Popup FP — apare DUPĂ review, doar dacă există angajați fără fișa postului încă.
          Închiderea (X/backdrop) revine la review, ca să nu se atribuie din greșeală. */}
      <Modal
        open={showFpModal}
        onClose={() => {
          setShowFpModal(false);
          setShowPreview(true);
        }}
        title={t('assign.fpModal.title')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => doAssign(false)} disabled={loading}>
              {t('assign.fpModal.no')}
            </Button>
            <Button variant="primary" onClick={() => doAssign(true)} loading={loading}>
              {t('assign.fpModal.yes')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t('assign.fpModal.message', { count: previewData?.summary?.needJobDescription || 0 })}
        </p>
      </Modal>

      {/* Modal rezultat atribuire */}
      <Modal
        open={!!assignResult}
        onClose={() => setAssignResult(null)}
        title={
          assignResult?.error ? t('assign.resultModalErrorTitle') : t('assign.resultModalTitle')
        }
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setAssignResult(null)}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        {assignResult?.error ? (
          <Alert type="error">{assignResult.error}</Alert>
        ) : (
          assignResult && (
            <div className="space-y-4">
              {(assignResult.created || 0) > 0 && (
                <div className="flex items-center gap-3 p-4 bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-700 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-accent-100 dark:bg-accent-950/50 flex items-center justify-center text-accent-600 dark:text-accent-300 flex-shrink-0">
                    <svg
                      className="w-7 h-7"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-accent-900 dark:text-accent-300 text-lg">
                      {assignResult.created > 1
                        ? t('assign.resultCreatedMany', { count: assignResult.created })
                        : t('assign.resultCreatedOne', { count: assignResult.created })}
                    </p>
                    <p className="text-accent-700 dark:text-accent-300 text-sm">
                      {t('assign.resultEmployeesNotified')}
                    </p>
                  </div>
                </div>
              )}

              {assignResult.created === 0 && (
                <Alert type="warning">{t('assign.resultNoNew')}</Alert>
              )}

              {assignResult.skipReasons?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {t('assign.resultWarningsTitle', { count: assignResult.skipReasons.length })}
                  </p>
                  {assignResult.skipReasons.map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-2 p-2.5 rounded-lg border text-sm ${
                        r.type === 'ADMIN_NO_ILM'
                          ? 'bg-danger-50 dark:bg-danger-950/50 border-danger-200 dark:border-danger-700 text-danger-800 dark:text-danger-300'
                          : 'bg-warning-50 dark:bg-warning-950/50 border-warning-200 dark:border-warning-700 text-warning-800 dark:text-warning-300'
                      }`}
                    >
                      <svg
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      {r.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </Modal>

      {/* Modal PREVIEW — confirmare înainte de trimitere: cine primește + cine semnează, editabil. */}
      <Modal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        title={t('assign.preview.title')}
        size="2xl"
        footer={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {previewData
                ? t('assign.preview.summary', {
                    recipients: previewData.summary.totalRecipients,
                    assignments: previewData.summary.willAssign,
                  })
                : ''}
              {previewData?.summary.willSkip > 0 && (
                <span className="text-warning-700 dark:text-warning-300 font-medium">
                  {' '}
                  · {t('assign.preview.willSkip', { count: previewData.summary.willSkip })}
                </span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowPreview(false)}>
                {t('assign.preview.back')}
              </Button>
              <Button
                variant="primary"
                loading={loading}
                disabled={previewLoading || !previewData || previewData.summary.willAssign === 0}
                onClick={confirmPreview}
              >
                {!loading && <SendIcon />}
                {t('assign.preview.confirmSend')}
              </Button>
            </div>
          </div>
        }
      >
        {previewLoading ? (
          <Spinner label={t('assign.preview.loading')} />
        ) : !previewData || previewData.recipients.length === 0 ? (
          <EmptyState icon={<UserEmptyIcon />} title={t('assign.preview.empty')} />
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <p className="text-sm text-slate-600 dark:text-slate-300 flex-1 min-w-[200px]">
                {t('assign.preview.intro')}
              </p>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {shouldChooseClm() && (
                  <Button variant="outline" size="sm" onClick={editClmFromPreview}>
                    {t('assign.preview.editClm')}
                  </Button>
                )}
                {previewData?.recipients?.some((r) =>
                  r.courses.some(
                    (c) => c.willAssign && (c.signers || []).some((s) => s.role === 'ADMIN')
                  )
                ) && (
                  <Button variant="outline" size="sm" onClick={openAdminModal}>
                    {t('assign.preview.editAdmin')}
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1 custom-scrollbar">
              {previewData.recipients.map((rec) => (
                <div
                  key={rec.employeeId}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                        {rec.name}
                      </span>
                      <RoleBadge role={rec.role} />
                      {rec.department && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          · {rec.department}
                        </span>
                      )}
                    </div>
                    {/* „×" — scoate destinatarul greșit din lot înainte de trimitere. */}
                    <button
                      onClick={() => removeRecipientFromPreview(rec.employeeId)}
                      title={t('assign.preview.removeRecipient')}
                      className="text-slate-400 hover:text-danger-600 dark:hover:text-danger-400 transition-colors flex-shrink-0"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-2">
                    {rec.courses.map((course) => (
                      <div
                        key={course.courseId}
                        className="text-sm pl-2 border-l-2 border-slate-100 dark:border-slate-800"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            {course.title}
                          </span>
                          {!course.willAssign && (
                            <Badge color="yellow" className="text-[10px] flex items-center gap-1">
                              <WarningIcon />
                              {t('assign.preview.skipped')}
                            </Badge>
                          )}
                        </div>

                        {!course.willAssign && course.warning ? (
                          <p className="text-xs text-warning-700 dark:text-warning-300 mt-0.5">
                            {course.warning.message}
                          </p>
                        ) : course.isNotCompleted ? (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 italic">
                            {t('assign.preview.noSheet')}
                          </p>
                        ) : (
                          <ul className="mt-1 space-y-0.5">
                            {course.signers.map((slot, idx) => (
                              <li key={idx} className="flex items-baseline gap-1.5 text-xs">
                                <span className="text-slate-500 dark:text-slate-400 font-medium min-w-[150px] flex-shrink-0">
                                  {signerRoleLabel(slot.role)}:
                                </span>
                                {renderSlotPeople(slot)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal desemnare CLM — alegerea conducătorilor responsabili pe departament (câștigă primul). */}
      <Modal
        open={showClmModal}
        onClose={cancelClmModal}
        title={t('clm.assign.title')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={cancelClmModal}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" loading={loading} onClick={openPreview}>
              {t('clm.assign.confirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">{t('clm.assign.intro')}</p>
          {clmDeptGroups.length > 1 && (
            <button
              onClick={selectAllClmEverywhere}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              {t('clm.assign.selectAllEverywhere')}
            </button>
          )}
          {clmDeptGroups.map((g) => {
            const scope = clmScopeByGroup[g.key] || groupDefaultScope(g);
            const eligible = eligibleForScope(scope);
            const sel = clmSelections[g.key] || [];
            const groupTraineeIds = traineeUserIdsInGroup(g);
            return (
              <div
                key={g.key}
                className="border border-slate-200 dark:border-slate-700 rounded-lg p-3"
              >
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  {t('clm.assign.employeesInDept', {
                    count: g.count,
                    dept: g.deptName || t('clm.assign.noDepartment'),
                  })}
                </p>
                {/* Bara de scope cu search: departamentul angajatului e precompletat; „Toată firma" → toți CLM. */}
                <div className="mb-2">
                  <SearchableSelect
                    value={scope}
                    onChange={(v) => changeGroupScope(g, v)}
                    placeholder={t('clm.assign.scopePlaceholder')}
                    options={clmScopeOptions}
                  />
                </div>
                {eligible.length > 0 && (
                  <div className="flex items-center justify-end gap-3 mb-1">
                    <button
                      onClick={() => selectAllInGroup(g)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      {t('clm.assign.selectAll')}
                    </button>
                    <button
                      onClick={() => clearGroup(g)}
                      className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400"
                    >
                      {t('clm.assign.clearGroup')}
                    </button>
                  </div>
                )}
                {eligible.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">{t('clm.assign.noClmForDept')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {eligible.map((m) => {
                      const checked = sel.includes(m.userId);
                      const isSelfTrainee = groupTraineeIds.has(m.userId);
                      return (
                        <label
                          key={m.userId}
                          title={isSelfTrainee ? t('clm.assign.selfTraineeHint') : undefined}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-sm ${
                            checked
                              ? 'bg-primary-50 dark:bg-primary-950/50 border-primary-300 dark:border-primary-700'
                              : 'border-slate-200 dark:border-slate-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleClm(g.key, m.userId)}
                            className="h-4 w-4 text-primary-600 rounded"
                          />
                          {m.user ? `${m.user.lastName} ${m.user.firstName}` : '—'}
                          {isSelfTrainee && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 italic">
                              {t('clm.assign.selfTraineeBadge')}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>

      {/* Modal desemnare ADMIN — alegerea administratorului care semnează slotul de verificare. */}
      <Modal
        open={showAdminModal}
        onClose={cancelAdminModal}
        title={t('assign.preview.adminModalTitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={cancelAdminModal}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={confirmAdminModal}>
              {t('clm.assign.confirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('assign.preview.adminModalIntro')}
          </p>
          {adminCandidatesLoading ? (
            <Spinner label={t('common.loading')} />
          ) : adminCandidates.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-400 italic">
              {t('assign.preview.adminNone')}
            </p>
          ) : (
            <>
              <button
                onClick={() => setAdminSelections([])}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                {t('assign.preview.adminAny')}
              </button>
              <div className="flex flex-wrap gap-2">
                {adminCandidates.map((a) => {
                  const checked = adminSelections.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-sm ${
                        checked
                          ? 'bg-primary-50 dark:bg-primary-950/50 border-primary-300 dark:border-primary-700'
                          : 'border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAdminSel(a.id)}
                        className="h-4 w-4 text-primary-600 rounded"
                      />
                      {a.name || '—'}
                      {a.isSignatureResponsible && (
                        <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                          {t('assign.preview.adminResponsible')}
                        </span>
                      )}
                      {a.empowered && (
                        <span className="text-[10px] text-amber-600 dark:text-amber-400">
                          {t('assign.preview.adminEmpowered')}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {t('assign.preview.adminHint')}
              </p>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AssignCourses;
